import {
  FC,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useBookTopSlotMutation } from '../../api/queries/topslot';
import Alert from '../../components/Alert/Alert';
import DateBar from '../../components/DateBar';
import { LoadingImages } from '../../components/LoadingImages/LoadingImages';
import Navigation from '../../components/Navigation/Navigation';
import NavigationButton from '../../components/Navigation/NavigationButton';
import { StepsUpdateContext } from '../../components/Steps/StepsContext';
import { TopSlotInfo } from '../../components/TopSlotInfo/TopSlotInfo';
import { TicketContext } from '../../contexts/TicketContext';
import DateTime from '../../helpers/DateTime';
import { useScrollToTop } from '../../helpers/hooks/useScrollToTop';
import { Stylesheet } from '../../interfaces/Stylesheet';
import { Ticket } from '../../interfaces/Ticket';
import { SlotsDictionary, TopSlot } from '../../interfaces/TopSlot';
import {
  selectedTopSlotIndexGA,
  sendAnalyticsDateSelected,
  sendReloadOnSchedulingError,
  successfullyBookedAppointmentGA,
} from '../../services/Analytics';
import styles from './Appointment.module.scss';
import AppointmentConfirmationText from './AppointmentConfirmationText';
import { scrollToElement } from './AppointmentController';

interface AppointmentWithTopSlotsProps {
  areLoadingSlots: boolean;
  canRetrieveMore: boolean;
  highestSlot: TopSlot;
  maxDate: Date;
  minDate: Date;
  onLoadMore: () => void;
  onSlotBooking: (pending: boolean) => void;
  stylesheet: Stylesheet;
  ticket: Ticket;
  topSlots: SlotsDictionary;
}

const AppointmentWithTopSlots: FC<AppointmentWithTopSlotsProps> = (props) => {
  const {
    ticket,
    stylesheet,
    topSlots,
    minDate,
    maxDate,
    highestSlot,
    onLoadMore,
    onSlotBooking,
    areLoadingSlots,
    canRetrieveMore,
  } = props;

  const [selectedTopSlot, setSelectedTopSlot] = useState<TopSlot | null>(highestSlot);
  const [selectedDate, setSelectedDate] = useState<string>(highestSlot.start.slice(0, 10));
  const [hasBookingError, setHasBookingError] = useState<boolean>(false);
  const { bookTopSlot, isPending } = useBookTopSlotMutation();

  const { completeCurrentStep, navigateToNextStep } = useContext(StepsUpdateContext);
  const { fetchTicket, setShowFeedback } = useContext(TicketContext);

  const navigationButtonsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  useScrollToTop();

  const handleNextClick = async () => {
    if (!selectedTopSlot) {
      return;
    }

    const { hashId, workOrder: { appointment: { id } } } = ticket;
    onSlotBooking(true);

    try {
      await bookTopSlot({ hashId, appointmentId: id, selectedTopSlot })
        .then(async () => {
          // This is needed so on the next screen (confirm) new required (arrivalWindowStart ...) fields are available right away
          await fetchTicket(hashId);
          successfullyBookedAppointmentGA(selectedTopSlot);
          // This is to only show the survey right after resident creates appoitnment or see noTopSlots.
          // If they revisit the confirmation page after a while they shouldnt be able to submit feedback
          setShowFeedback(true);
        })
        .finally(() => {
          onSlotBooking(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });

      completeCurrentStep();
      navigateToNextStep();
    } catch {
      setHasBookingError(true);
    }
  };

  const selectDate = useCallback((date: string) => {
    if (selectedTopSlot && date !== selectedTopSlot.start.slice(0, 10)) {
      setSelectedTopSlot(null);
    } else {
      sendAnalyticsDateSelected(date);
    }
    setSelectedDate(date);
  }, [selectedTopSlot]);

  const selectSlot = useCallback((slot: TopSlot, index: number) => {
    setSelectedTopSlot(slot);
    selectedTopSlotIndexGA(index);
  }, []);

  if (isPending) {
    return (
      <LoadingImages
        items={[
          {
            image: 'slotBooking',
            text: t('appointmentSection:bookingText'),
            description: t('appointmentSection:moment'),
            ariaLabel: t('appointmentSection:bookingText'),
          },
        ]}
      />
    );
  }

  return (
    <>
      {hasBookingError && (
        <Alert
          level="error"
          text={t('alert:bookingError')}
          textButton={{
            text: t('alert:refreshPage'),
            onClick: () => {
              sendReloadOnSchedulingError();
              setTimeout(() => {
                window.location.reload();
                // timeout to give GA a chance to send the event
              }, 100);
            },
          }}
          white />
      )}

      <>
        <DateBar
          areLoadingSlots={areLoadingSlots}
          canRetrieveMore={canRetrieveMore}
          dates={DateTime.rangeDays(minDate, maxDate)}
          highestSlot={highestSlot}
          onLoadMore={onLoadMore}
          selectedDate={selectedDate}
          setSelectedDate={selectDate}
          slots={topSlots}
        />
        <div>
          {!!topSlots[selectedDate] && !areLoadingSlots && topSlots[selectedDate].map((topSlot: TopSlot, index: number) => (
            <TopSlotInfo
              active={topSlot === selectedTopSlot}
              isOptimal={topSlot.start === highestSlot.start}
              key={index}
              onSelect={(): void => {
                selectSlot(topSlot, index);
                scrollToElement(navigationButtonsRef.current);
              }}
              topSlot={topSlot}
            />
          ))}
        </div>
      </>
      {!!selectedTopSlot && (
        <AppointmentConfirmationText
          selectedTopSlot={selectedTopSlot}
          stylesheet={stylesheet}
          ticket={ticket}
        />
      )}

      <div className={styles.appointmentContainer}>
        <Navigation className={styles.fullButtonMobile} rootRef={navigationButtonsRef}>
          <NavigationButton
            buttonAction="next"
            nextDisabled={isPending || areLoadingSlots || !selectedTopSlot}
            onClickOverride={handleNextClick}
            text={t('appointmentSection:confirmAppointment')}
          />
        </Navigation>
      </div>
    </>
  );
};

export default AppointmentWithTopSlots;
